import React, { Fragment, useContext } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { I18nProvider, getContext } from '@fiverr-private/fiverr_context';
import { DEFAULT_LOCALE } from '../../../../shared/constants';
import { includesAds } from '../../utils/index';
import { SearchPropTypes } from '../../types/index';
import getLogoMakerBannerConfig from '../../config/logoMakerBannerConfig';
import { DynamicGigListings, ProFirstGigListings, Pagination, RecommendationsCarousels, SelectedFilters, EmptySearchResults, NumberOfResults, SortBy, TopBarFilters, MobileFilters, NoResults } from '../common';
import StickyComponent from '../hoc/stickyComponent';
import MachineTranslationButtonWrapper from '../common/machine_translation_button_wrapper';
import { MINIMUN_TILES_LENGTH } from '../common/tiles_carousel/constants';
import ScTilesCarousel from '../common/sc_tiles_carousel';
import TopFiltersCarousel from '../common/top_filters_carousel';
import Modalities from '../common/modalities/Modalities';
import { AppContext } from '../context/listingsContext';
import { getFilterTilesCarousel } from '../common/top_filters_carousel/utils';
import { getScTilesData } from '../common/sc_tiles_carousel/utils';
import { DOMINATE_SC_FOR_SEARCH } from '../../abTestAllocations/navigationTilesSearch/constants';
import { EXPERIMENTS } from '../../../../shared/constants/experiments';
import { isAllocated } from '../../../../shared/utils/experimentService';
import SearchResultsCounter from './search_results_counter';
import RelatedSearch from './related_search';
import SearchHeader from './search_header';
import GuidedSearchNav from './guided_search_nav';

const Search = (props) => {

    const {
        pagination,
        sorting,
        displayData,
        topBarSorting,
        loggedIn
    } = props;

    const { abTests = {} } = getContext();

    const {
        isTouch,
        listings: [listings],
        inUgcTranslationsRollout,
        inSearchNavigationTiles,
        isLMBannerInSearchAllowed,
        dominateSubCategoryId,
        filter: appFilters,
        significantLeafCategories = []
    } = useContext(AppContext);

    const { gigs, choiceModalities, proData, context: { sessionLocale, abTestsProps, pageCtxId }, showListViewCards } = listings;
    const { active, selected, topBar, sideBar, subCategory } = appFilters;
    const { inGuidedSearchNavExp, inPromoteMTCtaExpCGroup } = abTestsProps;
    const noGigs = !gigs.length > 0 && !proData;
    const isEmptySearchBase = proData ? noGigs && proData.proGigs === 0 : noGigs;
    const isEmptySearch = _.isEmpty(active) && isEmptySearchBase;
    const hasFilters = sideBar.length > 0;
    const selectedSorting = topBarSorting.find((sort) => sort.selected) || topBarSorting[0];
    const listingsWithAds = includesAds(listings.gigs) || (!!proData && includesAds(proData.proGigs));
    const scTilesData = getScTilesData(significantLeafCategories, subCategory);
    const subCatFiltersCarousel = getFilterTilesCarousel(dominateSubCategoryId);
    const shouldShowModalities = !_.isEmpty(choiceModalities) && !_.isEmpty(choiceModalities.buckets);
    const shouldShowFiltersCarousel = inSearchNavigationTiles && !shouldShowModalities && DOMINATE_SC_FOR_SEARCH.includes(dominateSubCategoryId);
    const shouldShowScCarousel = !shouldShowFiltersCarousel && inSearchNavigationTiles && !shouldShowModalities && scTilesData.length >= MINIMUN_TILES_LENGTH;
    const shouldShowRelatedSearch = !inGuidedSearchNavExp && !inSearchNavigationTiles && sessionLocale === DEFAULT_LOCALE;
    let selectedScName = subCategory ? subCategory.alias : '';

    if (subCategory && !subCategory.selected) {
        const [item] = subCategory.children.filter((item) => item.selected);
        selectedScName = item ? item.alias : selectedScName;
    }

    const hasGuidedSearchTerms = displayData.guidedSearchNavData && displayData.guidedSearchNavData.length;
    const shouldRenderGuidedSearch = sessionLocale === DEFAULT_LOCALE && inGuidedSearchNavExp;
    const displayTranslationButton = sessionLocale !== DEFAULT_LOCALE && inUgcTranslationsRollout;

    const shouldShowBanner = isLMBannerInSearchAllowed && isAllocated(EXPERIMENTS.SPIDERS_BANNER_MPI_SEARCH, abTests);
    const { logoMakerBanner, logoMakerBannerConfig } = getLogoMakerBannerConfig({
        shouldShowBanner,
        isTouch,
        showListViewCards
    });

    return (
        <div className={classNames('search-page', { 'top-filters': !isTouch })}>
            {!isEmptySearch ?
                <Fragment>
                    {isTouch && <Fragment>
                        {shouldRenderGuidedSearch && <GuidedSearchNav terms={displayData.guidedSearchNavData} query={displayData.searchComponentData.query} pageCtxId={pageCtxId}/>}

                        <I18nProvider>
                            {({ i18n }) => (
                                <SearchResultsCounter paginationData={pagination} displayData={displayData} i18n={i18n}/>
                            )}
                        </I18nProvider>
                    </Fragment>}

                    {shouldShowRelatedSearch &&
                    <RelatedSearch displayData={displayData}/>}
                    {isTouch && hasFilters &&
                        <>
                            <StickyComponent>
                                <MobileFilters activeFilters={active}
                                    sorts={topBarSorting}
                                    filters={sideBar}/>
                            </StickyComponent>
                            {displayTranslationButton &&
                            <MachineTranslationButtonWrapper gigsListings={[listings.gigs]}
                                showSeparator={false}/>}
                        </>
                    }

                    {!isTouch &&
                        <Fragment>
                            <div className="row header-wrapper">
                                <SearchHeader subCategoryData={subCategory} searchData={displayData.searchComponentData} hasGuidedSearchTerms={hasGuidedSearchTerms} inGuidedSearchNavExp={inGuidedSearchNavExp}/>
                            </div>
                            {
                                shouldShowFiltersCarousel &&
                                <div className="row">
                                    <TopFiltersCarousel topFiltersCarousel={subCatFiltersCarousel} subCategoryId={dominateSubCategoryId}/>
                                </div>
                            }
                            {
                                shouldShowScCarousel &&
                                <div className="row">
                                    <ScTilesCarousel scTilesData={scTilesData}/>
                                </div>
                            }
                            {shouldRenderGuidedSearch && <GuidedSearchNav terms={displayData.guidedSearchNavData} query={displayData.searchComponentData.query} pageCtxId={pageCtxId}/>}
                            <TopBarFilters filters={topBar}
                                selectedFilters={selected}
                                activeFilters={active}
                                subCategory={subCategory}
                                noGigs={noGigs}
                                fromSearchFlow/>

                            <div className="selected-filters-wrapper row">
                                <SelectedFilters selectedFilters={selected}
                                    activeFilters={active}
                                    selectedScName={selectedScName}/>
                            </div>

                            {!noGigs && <div className="row top-secondary">
                                <div className={classNames('results-info', { 'flex-center': inPromoteMTCtaExpCGroup })}>
                                    <NumberOfResults pagination={pagination} withAds={listingsWithAds}/>
                                    {displayTranslationButton &&
                                        <MachineTranslationButtonWrapper gigsListings={[listings.gigs]}
                                            showSeparator/>}
                                </div>
                                <SortBy title={selectedSorting.alias}
                                    data={topBarSorting}/>
                            </div>}
                        </Fragment>}
                    { shouldShowModalities &&
                    <div className="row modalities-row">
                        <Modalities listing={listings}/>
                    </div>
                    }
                    <div className="row content-row">
                        {noGigs && <NoResults/>}
                        <div className="content">
                            {proData ?
                                <ProFirstGigListings listings={listings}/> :
                                <DynamicGigListings listings={listings}
                                    bannerConfig={logoMakerBannerConfig}
                                    banner={logoMakerBanner}/>
                            }
                        </div>

                        {window && window.location && <Pagination pagination={pagination}/>}
                    </div>
                </Fragment> :
                <EmptySearchResults displayData={displayData} loggedIn={loggedIn}/>
            }
            {(!isTouch && loggedIn) && <RecommendationsCarousels sorting={sorting} activeFilters={active} loggedIn={loggedIn}/>}
        </div>
    );
};

Search.propTypes = SearchPropTypes;

export default Search;
